import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useAiConversations } from '@/hooks/queries/useAi';
import { useDeleteConversation, useRenameConversation } from '@/hooks/mutations/useAiMutations';
import type { AiConversation } from '@/types/ai';
import { cleanConversationTitle } from '@/lib/aiMessage';
import { cn } from '@/lib/utils';

interface ConversationListProps {
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
  selectedConversationId?: string | null;
  onConversationDeleted?: (id: string) => void;
}

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const diffMinutes = Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 60_000));
  if (diffMinutes < 1) return 'now';
  if (diffMinutes < 60) return `${diffMinutes}m`;
  const hours = Math.floor(diffMinutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function groupByPeriod(conversations: AiConversation[]) {
  const now = new Date();
  const today: AiConversation[] = [];
  const week: AiConversation[] = [];
  const older: AiConversation[] = [];

  conversations.forEach((conversation) => {
    if (!conversation.last_message_at) {
      older.push(conversation);
      return;
    }

    const date = new Date(conversation.last_message_at);
    const diffMs = now.getTime() - date.getTime();
    const diffDays = diffMs / (24 * 60 * 60 * 1000);

    if (diffDays < 1) {
      today.push(conversation);
      return;
    }
    if (diffDays <= 7) {
      week.push(conversation);
      return;
    }
    older.push(conversation);
  });

  return { today, week, older };
}

function ConversationItem({
  conversation,
  selected = false,
  pendingDelete = false,
  onDelete,
  onRename,
  onClick,
}: {
  conversation: AiConversation;
  selected?: boolean;
  pendingDelete?: boolean;
  onDelete: () => void;
  onRename: (title: string) => void;
  onClick: () => void;
}) {
  const { t } = useTranslation('dashboard');
  const title = cleanConversationTitle(conversation.title) ?? t('chat_new');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  function startEditing(event: React.MouseEvent) {
    event.stopPropagation();
    setDraft(title);
    setEditing(true);
    window.setTimeout(() => inputRef.current?.select(), 0);
  }

  function commit() {
    setEditing(false);
    const next = draft.trim();
    if (next !== '' && next !== title) {
      onRename(next);
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (editing) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'w-full text-left flex items-start gap-3 p-3 rounded-lg border border-neutral-200 dark:border-charcoal-700 group',
        selected
          ? 'bg-secondary-50 border-secondary-300'
          : 'bg-white dark:bg-card hover:bg-neutral-50 dark:hover:bg-charcoal-800',
        pendingDelete && 'opacity-50',
      )}
    >
      <MessageCircle className="size-4 text-neutral-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            maxLength={100}
            onChange={(event) => setDraft(event.target.value)}
            onClick={(event) => event.stopPropagation()}
            onBlur={commit}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === 'Enter') commit();
              if (event.key === 'Escape') setEditing(false);
            }}
            className="w-full rounded border border-neutral-300 bg-white px-1.5 py-0.5 text-sm font-medium text-charcoal-900 focus:outline-none focus:ring-1 focus:ring-lime-400 dark:border-charcoal-600 dark:bg-charcoal-900 dark:text-charcoal-50"
            aria-label={t('chat_rename', { defaultValue: 'Renommer la conversation' })}
          />
        ) : (
          <p className="text-sm font-medium text-charcoal-900 truncate">{title}</p>
        )}
        <p className="text-xs text-neutral-400 mt-0.5">
          {relativeTime(conversation.last_message_at)}
        </p>
      </div>
      <button
        type="button"
        onClick={startEditing}
        className="rounded border border-neutral-200 dark:border-charcoal-700 bg-white dark:bg-card p-1 text-neutral-500 hover:text-charcoal-900 hover:border-charcoal-400 lg:opacity-0 lg:group-hover:opacity-100 lg:focus-visible:opacity-100 transition-opacity"
        aria-label={t('chat_rename', { defaultValue: 'Renommer la conversation' })}
      >
        <Pencil className="size-3.5" />
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onDelete();
        }}
        className="rounded border border-neutral-200 dark:border-charcoal-700 bg-white dark:bg-card p-1 text-neutral-500 hover:text-error hover:border-error"
        aria-label={t('chat_delete')}
      >
        <Trash2 className="size-3.5" />
      </button>
    </div>
  );
}

export function ConversationList({
  onSelectConversation,
  onNewConversation,
  selectedConversationId = null,
  onConversationDeleted,
}: ConversationListProps) {
  const { t } = useTranslation('dashboard');
  const { data: conversations, isLoading } = useAiConversations();
  const deleteConversation = useDeleteConversation();
  const renameConversation = useRenameConversation();
  const [query, setQuery] = useState('');
  const [optimisticDeletes, setOptimisticDeletes] = useState<Record<string, AiConversation>>({});
  const [conversationToDelete, setConversationToDelete] = useState<AiConversation | null>(null);

  const filteredConversations = useMemo(() => {
    if (!conversations) return [];
    const normalized = query.trim().toLowerCase();
    if (!normalized) return conversations;
    return conversations.filter((conversation) =>
      (cleanConversationTitle(conversation.title) ?? '').toLowerCase().includes(normalized),
    );
  }, [conversations, query]);

  useEffect(() => {
    if (!conversations) {
      return;
    }

    const currentIds = new Set(conversations.map((conversation) => conversation.id));
    setOptimisticDeletes((prev) => {
      let changed = false;
      const next: Record<string, AiConversation> = {};

      for (const [id, value] of Object.entries(prev)) {
        if (currentIds.has(id)) {
          next[id] = value;
          continue;
        }
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [conversations]);

  const visibleConversations = useMemo(
    () =>
      filteredConversations.filter(
        (conversation) => optimisticDeletes[conversation.id] === undefined,
      ),
    [filteredConversations, optimisticDeletes],
  );

  const grouped = useMemo(() => groupByPeriod(visibleConversations), [visibleConversations]);

  async function deleteImmediately(conversation: AiConversation) {
    if (optimisticDeletes[conversation.id] !== undefined) {
      return;
    }

    setOptimisticDeletes((prev) => ({ ...prev, [conversation.id]: conversation }));

    try {
      await deleteConversation.mutateAsync(conversation.id);
      onConversationDeleted?.(conversation.id);
    } catch (error) {
      setOptimisticDeletes((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([key]) => key !== conversation.id)),
      );
      toast.error(error instanceof Error ? error.message : t('chat_error'));
    }
  }

  function requestDelete(conversation: AiConversation) {
    if (optimisticDeletes[conversation.id] !== undefined) {
      return;
    }

    setConversationToDelete(conversation);
  }

  async function confirmDelete() {
    if (conversationToDelete === null) {
      return;
    }

    await deleteImmediately(conversationToDelete);
    setConversationToDelete(null);
  }

  function renderGroup(title: string, items: AiConversation[]) {
    if (items.length === 0) return null;
    return (
      <div className="space-y-2">
        <p className="px-1 text-[11px] font-medium text-neutral-500">{title}</p>
        {items.map((conv) => (
          <ConversationItem
            key={conv.id}
            conversation={conv}
            selected={selectedConversationId === conv.id}
            pendingDelete={Boolean(optimisticDeletes[conv.id])}
            onDelete={() => requestDelete(conv)}
            onRename={(title) =>
              renameConversation.mutate(
                { id: conv.id, title },
                {
                  onError: (error) =>
                    toast.error(error instanceof Error ? error.message : t('chat_error')),
                },
              )
            }
            onClick={() => onSelectConversation(conv.id)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="p-3.5 border-b border-neutral-200 dark:border-charcoal-700 bg-neutral-50/50 dark:bg-charcoal-850">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-heading text-sm font-semibold text-charcoal-900 dark:text-white">
            {t('chat_history', { defaultValue: 'Historique' })}
          </h3>
        </div>
        <button
          type="button"
          onClick={onNewConversation}
          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-primary/90 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:bg-primary dark:hover:bg-primary/90"
        >
          <Plus className="size-4" strokeWidth={2.2} aria-hidden />
          {t('chat_new')}
        </button>
        <div className="relative mt-2.5">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400 dark:text-charcoal-500" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('chat_search')}
            className="h-9 rounded-lg border-neutral-200 bg-white pl-9 text-xs placeholder:text-neutral-400 dark:border-charcoal-700 dark:bg-charcoal-800 dark:text-white dark:placeholder:text-charcoal-500"
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain p-3">
        {isLoading && <p className="text-sm text-neutral-400 text-center py-4">{t('loading')}</p>}

        {!isLoading && visibleConversations.length === 0 && (
          <p className="text-sm text-neutral-400 text-center py-8">{t('chat_empty')}</p>
        )}

        {!isLoading && (
          <>
            {renderGroup(t('chat_group_today'), grouped.today)}
            {renderGroup(t('chat_group_week'), grouped.week)}
            {renderGroup(t('chat_group_older'), grouped.older)}
          </>
        )}
      </div>

      <ConfirmDialog
        open={conversationToDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConversationToDelete(null);
          }
        }}
        title={t('chat_delete_confirm_title', { defaultValue: 'Supprimer cette conversation ?' })}
        description={t('chat_delete_confirm_description', {
          defaultValue: 'Cette action est irreversible.',
        })}
        confirmLabel={t('chat_delete_confirm', { defaultValue: 'Supprimer' })}
        cancelLabel={t('chat_delete_cancel', { defaultValue: 'Annuler' })}
        onConfirm={() => {
          void confirmDelete();
        }}
        variant="danger"
      />
    </div>
  );
}
